import moment from "moment/min/moment-with-locales";
import { withMomentLocale } from "./eventFormat";

/**
 * Coupon display helpers shared by the redeem centre and the QR scanner.
 */

export type RedeemedCouponRecord = {
  id: string;
  coupon_id?: string | null;
  used?: boolean | number | null;
  expiry_date?: string | null;
  coupon_info?: {
    id?: string | null;
    coupon_name?: string | null;
    coupon_image?: string | null;
    promo_code?: string | null;
    scan_by_qr_code?: boolean | number | null;
    terms_conditions_en?: string | null;
    terms_conditions_zh_hant?: string | null;
    user_info?: {
      business_profile?: {
        company_name?: string | null;
        contact_number?: string | null;
      } | null;
    } | null;
  } | null;
};

/**
 * `redeem_records.used` follows the RN semantics: `true` (1) is a fresh,
 * usable coupon and `false` (0) means it has been consumed by a shop scan.
 */
export const isRedeemedCouponUsed = (record: RedeemedCouponRecord) =>
  record?.used === false || record?.used === 0;

export const isRedeemedCouponExpired = (record: RedeemedCouponRecord) => {
  if (!record?.expiry_date) return false;
  const expiry = moment(record.expiry_date);
  return expiry.isValid() ? expiry.isBefore(moment()) : false;
};

export const formatCouponExpiry = (
  expiryDate?: string | null,
  language?: string,
) => {
  if (!expiryDate) return "";
  withMomentLocale(language ?? "en");
  const parsed = moment(expiryDate);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

/**
 * QR payload shown to the shop staff: `<redeemed id>&<coupon id>`. The business
 * scanner only splits on `&`, so the separator must match the RN payload.
 */
export const buildRedeemedCouponQrValue = (record: RedeemedCouponRecord) =>
  `${record?.id ?? ""}&${record?.coupon_info?.id ?? record?.coupon_id ?? ""}`;

/** Split a scanned redeemed-coupon QR payload into its two ids. */
export const parseRedeemedCouponQrValue = (value: string) => {
  const parts = String(value ?? "").split(/[&,]/).filter(Boolean);
  return parts.length >= 2 ? { id: parts[0], couponId: parts[1] } : null;
};

const REDEEM_MESSAGES: Record<number, string> = {
  [-30039]: "Can`t Redeem Coupon",
  [-30040]: "Not Enough Coupon To Redeem",
  [-30041]: "Can`t Get Score",
  [-30043]: "Not Enough Point",
  [-30061]: "Only students can redeem coupons",
  [-30066]: "Please upload both your HKID and student card images.",
  [-40003]: "User Can Only Redeem The Coupon Once",
  [-40031]: "This offer is suspended and cannot be redeemed right now.",
  [-40053]: "Redeemed Coupon Expired",
};

const SCAN_MESSAGES: Record<number, string> = {
  [-30045]: "Can`t Use Redeemed Coupons",
  [-30047]: "Redeemed Coupon Not Existed",
  [-30048]: "Redeemed Coupon Was Used",
  [-30055]: "Coupon Does Not Belong To This Account",
  [-40053]: "Redeemed Coupon Expired",
};

/** i18n key for a `redeemCoupon` response status (mirrors the RN alert text). */
export const getRedeemMessageKey = (status?: number) =>
  (status !== undefined ? REDEEM_MESSAGES[status] : undefined) ??
  "Can`t Redeem Coupon";

/** i18n key for a `businessScanToUseRedeemedCoupon` response status. */
export const getScanMessageKey = (status?: number) =>
  (status !== undefined ? SCAN_MESSAGES[status] : undefined) ??
  "Can`t Use Redeemed Coupons";
